import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TSC_CUSTOMERS, TscCustomer, formatTscCustomerId, formatTscPhone, isWithinWindow, placedLabel, tscStatus } from "../data/tscCustomers";
import { TscAgentToggle, TscAvatar, TscFooter, TscHeader } from "./TscChrome";
import { DEFAULT_TSC_DEMO, TscDemo, tscPickerHref } from "./tscDemos";

// Tractor Supply customer picker (/experience?agent=tsc): one list of customers, each
// with what they ordered and when. No scenarios or case labels on purpose; the caller
// chooses who to be, not which test to run.

function CustomerCard({ c, onSelect, verifyWith }: { c: TscCustomer; onSelect: (c: TscCustomer) => void; verifyWith: TscDemo["verifyWith"] }) {
  // The details Riley asks for come first, in the order she asks for them.
  const rows = verifyWith === "customerId"
    ? [
        { k: "Customer ID", v: formatTscCustomerId(c.id) },
        { k: "Billing zip", v: c.zip },
        { k: "Email (password reset)", v: c.email },
        { k: "Phone", v: formatTscPhone(c.phone) },
      ]
    : [
        { k: "Email", v: c.email },
        { k: "Billing zip", v: c.zip },
        { k: "Phone", v: formatTscPhone(c.phone) },
        { k: "Customer ID", v: c.id },
      ];
  return (
    <button type="button" className="tsc-card" onClick={() => onSelect(c)}>
      <div className="tsc-card-head">
        <TscAvatar src={c.photo} name={c.name} size={52} />
        <div className="tsc-card-who">
          <span className="tsc-card-name">{c.name}</span>
          <span className="tsc-card-meta">{c.city}, {c.state}</span>
          {c.neighborsClub && (
            <span className="tsc-badge">
              Neighbor's Club{c.ncReward ? ` · $${c.ncReward.usd} expires in ${c.ncReward.daysLeft} days` : ""}
            </span>
          )}
        </div>
        <span className="tsc-chip tsc-card-num">Customer {c.num}</span>
      </div>
      <dl className="tsc-card-rows">
        {rows.map((r) => (
          <div key={r.k}><dt>{r.k}</dt><dd>{r.v}</dd></div>
        ))}
      </dl>
      <div className="tsc-orders">
        <div className="tsc-orders-h">{c.orders.length === 1 ? "Order" : `Orders (${c.orders.length})`}</div>
        {c.orders.map((o) => (
          <div className="tsc-order" key={o.orderId}>
            <div className="tsc-order-r1">
              <span className="tsc-order-name">{o.product}</span>
              <span className="tsc-price">${o.totalUsd.toFixed(2)}</span>
            </div>
            <div className="tsc-order-r2">
              <span className={`tsc-placed${isWithinWindow(o) ? " tsc-placed-new" : ""}`}>Placed {placedLabel(o.ageMinutes)}</span>
              {isWithinWindow(o) && <span className="tsc-window">Within 59 min</span>}
              <span className="tsc-order-st">{tscStatus(o.status).label}</span>
            </div>
          </div>
        ))}
      </div>
      <span className="tsc-btn tsc-btn-primary tsc-card-cta">Start call</span>
    </button>
  );
}

export function TscCustomerPicker({ onSelect, demo = DEFAULT_TSC_DEMO }: { onSelect: (c: TscCustomer) => void; demo?: TscDemo }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const shown = useMemo(
    () => TSC_CUSTOMERS.filter((c) => !query || [c.name, c.email, c.id, formatTscCustomerId(c.id), c.city].some((s) => s.toLowerCase().includes(query))),
    [query],
  );

  return (
    <div className="tsc-ui">
      <TscHeader demo={demo} toggle={<TscAgentToggle demo={demo} onPick={(d) => navigate(tscPickerHref(d), { replace: true })} />} />
      <section className="tsc-section tsc-picker">
        <div className="tsc-wrap">
          <div className="tsc-picker-head">
            <div>
              <div className="tsc-eyebrow">Customers</div>
              <h1 className="tsc-h1 tsc-h1-sm">Pick a customer</h1>
              <p className="tsc-section-p">{`Choose who to call ${demo.persona.name} as. Each card shows the customer's details and their recent orders.`}</p>
            </div>
            <label className="tsc-search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={demo.verifyWith === "customerId" ? "Search by name, customer ID, email or town" : "Search by name, email or town"} spellCheck={false} />
            </label>
          </div>

          {shown.length > 0 ? (
            <div className="tsc-grid tsc-picker-grid">{shown.map((c) => <CustomerCard key={c.id} c={c} onSelect={onSelect} verifyWith={demo.verifyWith} />)}</div>
          ) : (
            <div className="tsc-panel tsc-empty">No customers match “{q.trim()}”.</div>
          )}
        </div>
      </section>
      <TscFooter />
    </div>
  );
}
